import { useState } from 'react'
import { useNavigate } from './nav'
import { Trash2, AlertTriangle } from 'lucide-react'
import type { Project } from '../data/projectTypes'
import { trackSegmentEvent } from '../lib/segment'
import { useDeleteProjectMutation } from '../services/projectDataApi'

interface DangerZoneProps {
  project: Project
}

export default function DangerZone({ project }: DangerZoneProps) {
  const [deleteProject] = useDeleteProjectMutation()
  const navigate = useNavigate()
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  async function handleDelete() {
    setDeleting(true)
    setDeleteError(null)
    try {
      await deleteProject(project.id).unwrap()
      trackSegmentEvent('Deleted Qa project', { projectId: project.id })
      navigate('/')
    } catch (err) {
      setDeleteError((err as { message?: string })?.message || 'Failed to delete project')
      setDeleting(false)
    }
  }

  return (
    <section className="settings-section settings-section--danger" data-testid="danger-zone">
      <h3 className="settings-section__title settings-section__title--danger">
        <AlertTriangle size={16} aria-hidden="true" /> Danger Zone
      </h3>
      {!showDeleteConfirm ? (
        <button
          type="button"
          className="settings-section__delete-btn"
          onClick={() => setShowDeleteConfirm(true)}
          data-analytics-event="project-settings-danger-zone-delete-button"
          data-testid="delete-project-btn"
        >
          <Trash2 size={14} aria-hidden="true" /> Delete Project
        </button>
      ) : (
        <div className="settings-section__delete-confirm" data-testid="delete-confirm">
          <p>Are you sure you want to delete <strong>{project.name}</strong>? This action cannot be undone.</p>
          {deleteError && (
            <p className="settings-section__delete-error" role="alert" data-testid="delete-error">{deleteError}</p>
          )}
          <div className="settings-section__delete-actions">
            <button type="button" onClick={() => setShowDeleteConfirm(false)} disabled={deleting} data-analytics-event="project-settings-danger-zone-delete-cancel">Cancel</button>
            <button
              type="button"
              className="settings-section__confirm-delete-btn"
              onClick={handleDelete}
              disabled={deleting}
              data-analytics-event="project-settings-danger-zone-delete-confirm"
              data-testid="confirm-delete-btn"
            >
              {deleting ? 'Deleting...' : 'Delete Project'}
            </button>
          </div>
        </div>
      )}
    </section>
  )
}
